/**
 * Frontend Attendance Calculation Utilities
 * Handles all attendance data processing that was moved from backend
 */

/**
 * Helper function to extract YYYY-MM-DD date string
 * Backend now sends dates in YYYY-MM-DD format, so this is mainly for consistency
 */
const extractDateString = (dateInput) => {
  if (!dateInput) return null;
  
  // Backend sends dates in YYYY-MM-DD format already
  if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateInput;
  }
  
  // Fallback for any other date format (shouldn't happen with current backend)
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate attendance statistics from raw session records
 */
export const calculateSessionStatistics = (records) => {
  if (!records || !Array.isArray(records)) {
    return {
      totalStudents: 0,
      presentCount: 0,
      absentCount: 0,
      tardyCount: 0,
      excusedCount: 0,
      attendanceRate: 0
    };
  }

  const totalStudents = records.length;
  const presentCount = records.filter(r => r.status === 'PRESENT').length;
  const absentCount = records.filter(r => r.status === 'ABSENT').length;
  const tardyCount = records.filter(r => r.status === 'LATE').length;
  const excusedCount = records.filter(r => r.status === 'EXCUSED').length;
  const attendanceRate = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0;

  return {
    totalStudents,
    presentCount,
    absentCount,
    tardyCount,
    excusedCount,
    attendanceRate
  };
};

/**
 * Group attendance records by date to get daily statistics
 */
export const groupRecordsByDate = (records) => {
  const grouped = {};
  
  records.forEach(record => {
    // Use the timezone-safe date extraction helper
    const date = extractDateString(record.date);
    if (!date) return; // Skip invalid dates
    
    if (!grouped[date]) {
      grouped[date] = {
        date,
        totalClasses: 0,
        present: 0,
        absent: 0,
        late: 0,
        excused: 0
      };
    }
    
    grouped[date].totalClasses++;
    const statusLower = record.status?.toLowerCase() || 'unknown';
    if (grouped[date][statusLower] !== undefined) {
      grouped[date][statusLower]++;
    }
  });
  
  // Calculate daily attendance score instead of rate
  Object.keys(grouped).forEach(date => {
    const dayData = grouped[date];
    const scoreValues = { present: 1, late: 0.5, absent: 0 };
    const dayScore = (dayData.present * scoreValues.present) + (dayData.late * scoreValues.late) + (dayData.absent * scoreValues.absent);
    const scorableClasses = dayData.totalClasses - (dayData.excused || 0); // Exclude excused from scoring
    const maxDayScore = scorableClasses * scoreValues.present;
    dayData.attendanceScore = maxDayScore > 0 ? dayScore : 0;
    dayData.maxScore = maxDayScore;
    dayData.attendanceScoreFraction = `${dayScore}/${maxDayScore}`;
    // Keep attendanceRate for backward compatibility
    dayData.attendanceRate = maxDayScore > 0 ? ((dayScore / maxDayScore) * 100).toFixed(2) : 0;
  });
  
  return grouped;
};

/**
 * Determine the primary status for chart display based on school day attendance logic:
 * - Green (present): If the first record of the day was Present
 * - Yellow (late): If the first record was Late OR if first record was Absent but student attends later classes
 * - Red (absent): If all records for the day are Absent
 */
const determinePrimaryStatus = (records, date) => {
  // Get all records for this date - need to convert record dates the same way as groupRecordsByDate
  const dayRecords = records.filter(record => {
    const recordDate = extractDateString(record.date);
    return recordDate === date;
  });
  
  if (dayRecords.length === 0) return 'absent';
  
  // Sort records by period start time to find the first class
  const sortedRecords = dayRecords.sort((a, b) => {
    const timeA = a.period?.startTime || '00:00:00';
    const timeB = b.period?.startTime || '00:00:00';
    return timeA.localeCompare(timeB);
  });
  
  const firstClass = sortedRecords[0];
  const firstClassStatus = firstClass.status?.toLowerCase();
  
  // Check if all records are absent
  const allAbsent = dayRecords.every(record => 
    record.status?.toLowerCase() === 'absent'
  );
  
  let result;
  
  // If all records are absent, show red
  if (allAbsent) {
    result = 'absent';
  }
  // If first class was present, show green
  else if (firstClassStatus === 'present') {
    result = 'present';
  }
  // If first class was late, OR first class was absent but student attended later, show yellow
  else if (firstClassStatus === 'late' || 
      (firstClassStatus === 'absent' && !allAbsent)) {
    result = 'late';
  }
  // Fallback (shouldn't reach here with current logic)
  else {
    result = 'absent';
  }
  
  return result;
};

/**
 * Format daily statistics for chart display
 */
export const formatForChart = (dailyStats, records = []) => {
  const dailyData = Object.values(dailyStats)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(day => ({
      date: day.date,
      attendanceScore: day.attendanceScore || 0, // Use the calculated score
      attendanceRate: parseFloat(day.attendanceRate), // Keep for backward compatibility
      present: day.present,
      absent: day.absent,
      late: day.late,
      excused: day.excused || 0,
      totalClasses: day.totalClasses,
      maxScore: day.maxScore || 0,
      primaryStatus: determinePrimaryStatus(records, day.date)
    }));

  return {
    daily: dailyData
  };
};

/**
 * Calculate overall summary statistics
 */
export const calculateSummary = (records, dailyStats) => {
  const totalDays = Object.keys(dailyStats).length;
  const totalClasses = records.length;
  const presentCount = records.filter(r => r.status?.toUpperCase() === 'PRESENT').length;
  const absentCount = records.filter(r => r.status?.toUpperCase() === 'ABSENT').length;
  const lateCount = records.filter(r => r.status?.toUpperCase() === 'LATE').length;
  const excusedCount = records.filter(r => r.status?.toUpperCase() === 'EXCUSED').length;
  
  // Calculate attendance score instead of rate
  // Present = 1 point, Late = 0.5 points, Absent = 0 points (Excused excluded from scoring)
  const scoreValues = { present: 1, late: 0.5, absent: 0 };
  const totalScore = (presentCount * scoreValues.present) + (lateCount * scoreValues.late) + (absentCount * scoreValues.absent);
  const scorableRecords = totalClasses - excusedCount; // Exclude excused from total possible score
  const maxPossibleScore = scorableRecords * scoreValues.present;
  const attendanceScorePercentage = maxPossibleScore > 0 ? ((totalScore / maxPossibleScore) * 100).toFixed(1) : 0;
  const attendanceScoreFraction = `${totalScore}/${maxPossibleScore}`;
  
  // Calculate total late minutes
  const totalLateMinutes = records.reduce((sum, record) => {
    return sum + (record.lateMinutes || 0);
  }, 0);

  // Create status counts for component
  const statusCounts = {
    present: presentCount,
    absent: absentCount,
    late: lateCount,
    excused: excusedCount
  };

  return {
    totalDays,
    totalClasses,
    totalRecords: totalClasses,
    presentCount,
    absentCount,
    lateCount,
    excusedCount,
    statusCounts,
    attendanceScore: attendanceScoreFraction, // New attendance score field as fraction
    attendanceScorePercentage: attendanceScorePercentage, // Percentage for calculations
    attendanceRate: attendanceScorePercentage, // Keep for backward compatibility  
    overallAttendanceRate: attendanceScorePercentage, // Keep for backward compatibility
    totalScore,
    maxPossibleScore,
    scorableRecords,
    earlyDepartureCount: 0, // This would need to be calculated if we have early departure data
    totalLateMinutes
  };
};

/**
 * Generate attendance patterns and insights
 */
export const generateAttendancePatterns = (records, dailyStats) => {
  console.log('Generating patterns for records:', records.length);
  
  if (!records || records.length < 5) {
    console.log('Not enough data for patterns:', records?.length || 0);
    return null; // Not enough data for meaningful patterns
  }

  const trends = [];
  const insights = [];
  const improvements = [];
  
  // Overall attendance trend
  const totalRecords = records.length;
  const presentCount = records.filter(r => r.status?.toUpperCase() === 'PRESENT').length;
  const attendanceRate = (presentCount / totalRecords) * 100;
  
  console.log('Attendance analysis:', { totalRecords, presentCount, attendanceRate });
  
  if (attendanceRate >= 95) {
    improvements.push({
      id: 'excellent',
      type: 'improvement',
      title: 'Excellent Attendance',
      description: `Maintains outstanding attendance with ${attendanceRate.toFixed(1)}% presence rate`,
      details: 'Keep up the excellent work!',
      severity: 'positive',
      actionable: false
    });
  } else if (attendanceRate >= 75) {
    trends.push({
      id: 'good',
      type: 'trend',
      title: 'Good Attendance Pattern',
      description: `Shows consistent attendance with ${attendanceRate.toFixed(1)}% presence rate`,
      details: 'Attendance is within acceptable range',
      severity: 'neutral',
      actionable: false
    });
  } else {
    insights.push({
      id: 'concerning',
      type: 'behavior',
      title: 'Attendance Concerns',
      description: `Low attendance rate of ${attendanceRate.toFixed(1)}% may need intervention`,
      details: 'Consider reaching out to student and parents',
      severity: 'warning',
      actionable: true
    });
  }

  // Late arrival pattern
  const lateCount = records.filter(r => r.status?.toUpperCase() === 'LATE').length;
  console.log('Late analysis:', { lateCount, threshold: totalRecords * 0.2 });
  
  if (lateCount > totalRecords * 0.2) { // More than 20% late
    insights.push({
      id: 'late-pattern',
      type: 'behavior',
      title: 'Frequent Late Arrivals',
      description: `${lateCount} late arrivals out of ${totalRecords} records (${((lateCount/totalRecords)*100).toFixed(1)}%)`,
      details: 'Monitor punctuality and address potential causes',
      severity: 'warning',
      actionable: true
    });
  }

  // Day of week pattern analysis
  const dayOfWeekPattern = analyzeDayPatterns(records);

  const patternsObject = {
    trends,
    insights,
    improvements,
    dayOfWeekPattern
  };

  console.log('Generated patterns object:', patternsObject);
  return patternsObject;
};

/**
 * Analyze attendance patterns by day of week
 */
export const analyzeDayPatterns = (records) => {
  const dayStats = {};
  
  records.forEach(record => {
    // Parse date safely to avoid timezone issues
    const dateStr = extractDateString(record.date);
    if (!dateStr) return;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    if (!dayStats[dayOfWeek]) {
      dayStats[dayOfWeek] = { total: 0, absent: 0, late: 0, present: 0 };
    }
    
    dayStats[dayOfWeek].total++;
    const status = record.status?.toUpperCase();
    if (status === 'ABSENT') {
      dayStats[dayOfWeek].absent++;
    } else if (status === 'LATE') {
      dayStats[dayOfWeek].late++;
    } else if (status === 'PRESENT') {
      dayStats[dayOfWeek].present++;
    }
  });

  // Convert to the format expected by the frontend component
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayPattern = {};
  
  Object.keys(dayStats).forEach(dayNum => {
    const stats = dayStats[dayNum];
    const dayName = dayNames[parseInt(dayNum)];
    
    if (stats.total >= 2) { // Only include days with at least 2 records
      dayPattern[dayName] = {
        total: stats.total,
        present: stats.present,
        absent: stats.absent,
        late: stats.late,
        attendanceRate: ((stats.present / stats.total) * 100).toFixed(1)
      };
    }
  });

  return Object.keys(dayPattern).length > 0 ? dayPattern : {};
};

/**
 * Process raw attendance data for frontend components
 * This is the main function that combines all calculations
 */
export const processAttendanceData = (rawData) => {
  if (!rawData || !rawData.records) {
    return null;
  }

  const { records, student, dateRange } = rawData;
  
  // Group records by date
  const dailyStats = groupRecordsByDate(records);
  
  // Format for charts
  const chartData = formatForChart(dailyStats, records);
  
  // Calculate summary
  const summary = calculateSummary(records, dailyStats);
  
  // Generate patterns
  const patterns = generateAttendancePatterns(records, dailyStats);
  
  return {
    student,
    dateRange,
    records,
    dailyStats,
    chartData,
    summary,
    patterns
  };
};
